import { gatewayApiAgent } from 'common/api/apiAgents';
import {
  ArchivalReason,
  BulkDocumentsCsvInfoResponse,
  BulkDocumentsCsvResponse,
  BulkUploadMetadata,
  BulkUploadResponse,
  CatalogUiFeedProduct,
  CatalogUiListMetaOuterCases,
  CatalogUiListMetaProducts,
  CatalogUiLocalizedBrand,
  CatalogUiOuterCase,
  CatalogUiProductAttributeMetadata,
  CatalogUiProductCountryLang,
  CopyProductResponse,
  CountriesDataFeedsAvailability,
  CountryVATsResponse,
  CreateCountryLangAttributesRequest,
  CreateOuterCaseRequest,
  CreateProductFromFeedRequest,
  CreateProductFromFeedResponse,
  CreateSuccessionRequest,
  CreateSuccessionResponse,
  DepositPermutationsResponse,
  ImagesMatchResponse,
  ImageUpdateRequestV2,
  LifecycleStatus,
  LifecycleTransitionRequest,
  LifecycleTransitionResponse,
  ListProducts,
  MerchantSearchRequest,
  MerchantSearchResponse,
  NutritionalMetadataListResponse,
  Product,
  ProductDownloadRequest,
  ProductFamiliesV2,
  ProductFeedConnectionsRequest,
  ProductFeedConnectionsResponse,
  ProductMerchantCatalogDownloadRequest,
  ProductReplacement,
  ProductTagsResponse,
  ReplaceProductRequest,
  SearchOuterCaseRequest,
  SearchProductRequest,
  StageProductRequest,
  UpdateOuterCaseRequest,
  UpdateProductWithFeedConnectionsRequest,
} from 'common/api-types/catalog';
import { CountryCode } from 'common/api-types/common';

export async function searchProducts({
  countryCodes,
  gtinlessOnly,
  page = 0,
  pageSize = 100,
  signal,
  sortBy,
  filter,
}: SearchProductRequest & { signal?: AbortSignal }) {
  const params: SearchProductRequest = {
    countryCodes,
    gtinlessOnly,
    page,
    pageSize,
    sortBy,
    filter,
  };

  const response = await gatewayApiAgent.post<CatalogUiListMetaProducts>(
    '/catalog/public/catalog-ui/v1/products/search',
    params,
    { signal },
  );

  return response.data;
}

export async function searchProductsByGtins(request: {
  countryCodes: CountryCode[];
  gtins: string[];
}) {
  const response = await gatewayApiAgent.post<ListProducts>(
    '/catalog/public/v1/products/by-gtins',
    request,
  );
  return response.data;
}

export async function fetchAttributeMetadata() {
  const response = await gatewayApiAgent.get<CatalogUiProductAttributeMetadata[]>(
    '/catalog/public/catalog-ui/v1/products/filter/attributes-metadata',
  );
  return response.data;
}

export const fetchProductById = (id: string) =>
  gatewayApiAgent.get<Product>(`/catalog/public/v1/products/${id}`).then((res) => res.data);

export const fetchCatalogUiProduct = (id: string, countryCode: CountryCode, languageCode: string) =>
  gatewayApiAgent
    .get<CatalogUiProductCountryLang>(
      `/catalog/public/catalog-ui/v1/products/${id}/${countryCode}/${languageCode}`,
    )
    .then((res) => res.data);

export const fetchNutritionalMetadata = () =>
  gatewayApiAgent
    .get<NutritionalMetadataListResponse>(`/catalog/public/v1/products/nutritional-metadata`)
    .then((res) => res.data);

export const replaceProductById = (productId: string, data: ReplaceProductRequest) =>
  gatewayApiAgent
    .post<ProductReplacement>(`/catalog/public/v1/products/${productId}/replacement`, data)
    .then((res) => res.data);

export async function uploadProductsCsv({
  file,
  createLifecycleStatus,
  updateLifecycleStatus,
  archivalReason,
  autoFeedDisconnect,
}: {
  file: File;
  createLifecycleStatus: LifecycleStatus | null;
  updateLifecycleStatus: LifecycleStatus | null;
  archivalReason: ArchivalReason | null;
  autoFeedDisconnect: boolean;
}) {
  const response = await gatewayApiAgent.post<BulkUploadResponse>(
    '/catalog/public/v2/products/upload',
    file,
    {
      headers: { 'Content-Type': 'text/csv' },
      params: { archivalReason, autoFeedDisconnect, createLifecycleStatus, updateLifecycleStatus },
    },
  );
  return response.data;
}

export async function fetchUploadMetadata() {
  const response = await gatewayApiAgent.get<BulkUploadMetadata>(
    '/catalog/public/v2/products/csv/metadata',
  );
  return response.data;
}

export const downloadProductsCsv = async (request: ProductDownloadRequest) =>
  gatewayApiAgent
    .post<string>(`/catalog/public/v1/products/download/csv`, request)
    .then((res) => res.data);

export const createProductTranslation = ({
  id,
  request,
}: {
  id: string;
  request: CreateCountryLangAttributesRequest;
}) =>
  gatewayApiAgent
    .post<Product>(`/catalog/public/v1/products/${id}`, request)
    .then((res) => res.data);

export const createStageProduct = (request: StageProductRequest) =>
  gatewayApiAgent
    .post<Product>(`/catalog/public/v1/products/stage`, request)
    .then((res) => res.data);

type ProductAttributesUpdateParams = { id: string; countryCode: CountryCode; languageCode: string };

export const updateProductAttributes = (
  { id, countryCode, languageCode }: ProductAttributesUpdateParams,
  request: UpdateProductWithFeedConnectionsRequest,
) =>
  gatewayApiAgent.post(
    `/catalog/public/catalog-ui/v1/products/${id}/countries/${countryCode}/languages/${languageCode}`,
    request,
  );

export async function matchMultipleImages({
  countryCode,
  request,
}: {
  countryCode: CountryCode;
  request: string[];
}) {
  const response = await gatewayApiAgent.post<ImagesMatchResponse>(
    `/catalog/public/v1/products/${countryCode}/images/match`,
    request,
  );
  return response.data;
}

export async function bulkUpdateMultipleImages({
  countryCode,
  request,
}: {
  countryCode: CountryCode;
  request: ImageUpdateRequestV2[];
}) {
  return gatewayApiAgent.put(
    `/catalog/public/v2/products/${countryCode}/images/bulk-update`,
    request,
  );
}

export async function fetchProductFamilies() {
  const response = await gatewayApiAgent.get<ProductFamiliesV2>(
    '/catalog/public/v2/product-families',
  );
  return response.data;
}

export const transitionLifecycleStatus = (request: LifecycleTransitionRequest) =>
  gatewayApiAgent
    .post<LifecycleTransitionResponse>(
      '/catalog/public/v1/products/lifecycle-status/transition',
      request,
    )
    .then((res) => res.data);

export const connectProductToFeed = (
  { countryCode }: { countryCode: CountryCode },
  request: ProductFeedConnectionsRequest,
) =>
  gatewayApiAgent
    .post<ProductFeedConnectionsResponse>(
      `/catalog/public/catalog-ui/v1/products/feed-connections/${countryCode}/product-connections`,
      request,
    )
    .then((res) => res.data);

export const disconnectProductFromFeed = ({
  countryCode,
  productId,
}: {
  countryCode: CountryCode;
  productId: string;
}) =>
  gatewayApiAgent
    .delete(
      `/catalog/public/catalog-ui/v1/products/${productId}/feed-connections/${countryCode}/product-connection`,
    )
    .then((res) => res.data);

export const fetchAvailableFeedsForCountries = () =>
  gatewayApiAgent
    .get<CountriesDataFeedsAvailability>('/catalog/public/catalog-ui/v1/feeds/available-countries')
    .then((res) => res.data);

export const fetchFeedProduct = ({
  gtin,
  countryCode,
}: {
  gtin: string;
  countryCode: CountryCode;
}) =>
  gatewayApiAgent
    .get<CatalogUiFeedProduct>(`/catalog/public/catalog-ui/v1/feed-products/${gtin}/${countryCode}`)
    .then((res) => res.data);

export const createProductFromFeed = (request: CreateProductFromFeedRequest) =>
  gatewayApiAgent
    .post<CreateProductFromFeedResponse>(
      '/catalog/public/catalog-ui/v1/products/create-product-from-feed',
      request,
    )
    .then((res) => res.data);

type CreateProductSuccessionProps = {
  countryCode: CountryCode;
  productId: string;
} & CreateSuccessionRequest;

export const createProductSuccession = ({
  countryCode,
  productId,
  successorId,
}: CreateProductSuccessionProps) =>
  gatewayApiAgent
    .post<CreateSuccessionResponse>(
      `/catalog/public/catalog-ui/v1/products/${productId}/${countryCode}/succession`,
      { successorId },
    )
    .then((res) => res.data);

export async function copyProduct({
  countryCode,
  productId,
  gtin,
}: {
  countryCode: CountryCode;
  productId: string;
  gtin: string;
}) {
  const response = await gatewayApiAgent.post<CopyProductResponse>(
    `/catalog/public/catalog-ui/v1/products/${productId}/${countryCode}/copy`,
    { gtin },
  );
  return response.data;
}

export async function assignProductFamily({
  productId,
  productFamilyId,
}: {
  productId: string;
  productFamilyId: string;
}) {
  const response = await gatewayApiAgent.post(
    `/catalog/public/v1/products/${productId}/families/${productFamilyId}`,
  );
  return response.data;
}

export async function fetchVATsForCountry({ countryCode }: { countryCode: CountryCode }) {
  const response = await gatewayApiAgent.get<CountryVATsResponse>(
    `/catalog/public/catalog-ui/v1/vat/${countryCode}`,
  );
  return response.data;
}

export async function fetchDepositPermutations(countryCode: CountryCode) {
  const response = await gatewayApiAgent.get<DepositPermutationsResponse>(
    `/catalog/public/v1/deposit-permutations/${countryCode}`,
  );
  return response.data;
}

export async function fetchBrands(languageCode: string) {
  const response = await gatewayApiAgent.get<CatalogUiLocalizedBrand[]>(
    `/catalog/public/catalog-ui/v1/brands/languages/${languageCode}`,
  );
  return response.data;
}

export async function fetchProductTags() {
  const response = await gatewayApiAgent.get<ProductTagsResponse>(
    `/catalog/public/catalog-ui/v1/product-tags`,
  );
  return response.data;
}

type DeleteSuccessionParams = {
  productId: string;
  countryCode: CountryCode;
  relatedProductId: string;
};

export async function deleteSuccession({
  productId,
  countryCode,
  relatedProductId,
}: DeleteSuccessionParams) {
  const response = await gatewayApiAgent.delete(
    `/catalog/public/catalog-ui/v1/products/${productId}/${countryCode}/succession/${relatedProductId}`,
  );
  return response.data;
}

export async function fetchOuterCase(id: string) {
  const response = await gatewayApiAgent.get<CatalogUiOuterCase>(
    `/catalog/public/catalog-ui/v1/outer-cases/${id}`,
  );
  return response.data;
}

export async function createOuterCase(request: CreateOuterCaseRequest) {
  const response = await gatewayApiAgent.post('/catalog/public/catalog-ui/v1/outer-cases', request);
  return response.data;
}

export async function updateOuterCase(id: string, request: UpdateOuterCaseRequest) {
  const response = await gatewayApiAgent.put(
    `/catalog/public/catalog-ui/v1/outer-cases/${id}`,
    request,
  );
  return response.data;
}

export async function deleteOuterCase(id: string) {
  const response = await gatewayApiAgent.delete(`/catalog/public/catalog-ui/v1/outer-cases/${id}`);
  return response.data;
}

export async function searchOuterCases(request: SearchOuterCaseRequest, signal?: AbortSignal) {
  const response = await gatewayApiAgent.post<CatalogUiListMetaOuterCases>(
    '/catalog/public/catalog-ui/v1/outer-cases/search',
    request,
    { signal },
  );
  return response.data;
}

export async function fetchProductDocumentsCsvMetadata() {
  const response = await gatewayApiAgent.get<BulkDocumentsCsvInfoResponse>(
    '/catalog/public/v1/products/documents/bulk/csv/metadata/full',
  );
  return response.data;
}

export async function uploadProductDocumentsCsv(csv: File) {
  const response = await gatewayApiAgent.post<BulkDocumentsCsvResponse>(
    '/catalog/public/v1/products/documents/bulk/csv',
    csv,
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    },
  );

  return response.data;
}

export const downloadMerchantCatalogProductsCsv = (
  request: ProductMerchantCatalogDownloadRequest,
) =>
  gatewayApiAgent
    .post<string>('/catalog/public/v1/products/download/merchant-catalog-csv', request)
    .then((res) => res.data);

export const searchMerchants = (request: MerchantSearchRequest) =>
  gatewayApiAgent
    .post<MerchantSearchResponse>('/catalog/public/merchant-ui/v1/merchants/search', request)
    .then((res) => res.data);
